import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useLocation } from '@remix-run/react';

type GuestItem = { name: string; price: number; quantity: number };
type PendingItem = GuestItem & { resolve: (add: boolean) => void };
const LoyaltyShoppingContext = createContext({
  guest: false,
  confirmAdd: async (_item: GuestItem) => true,
});

// Match the header's account indicator. The API still owns price eligibility.
function signedIn() {
  return document.cookie.split(';').some((cookie) => {
    const [name, ...value] = cookie.trim().split('=');
    return name === 'catalystUserId' && value.join('=') !== '';
  });
}
export function openLoyaltySignIn() {
  window.dispatchEvent(new Event('login:trigger'));
}

export function LoyaltyShoppingProvider({ children }: { children: ReactNode }) {
  const [guest, setGuest] = useState(false);
  const [pending, setPending] = useState<PendingItem | null>(null);
  const active = useRef<PendingItem | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const location = useLocation();
  const finish = useCallback((add: boolean) => {
    active.current?.resolve(add);
    active.current = null;
    dialog.current?.close();
    setPending(null);
  }, []);
  useEffect(() => {
    const refresh = () => setGuest(!signedIn());
    const cancel = () => finish(false);
    refresh();
    window.addEventListener('user:login', refresh);
    window.addEventListener('user:logout', refresh);
    window.addEventListener('store:change', cancel);
    return () => {
      window.removeEventListener('user:login', refresh);
      window.removeEventListener('user:logout', refresh);
      window.removeEventListener('store:change', cancel);
      active.current?.resolve(false);
    };
  }, [finish]);
  useEffect(() => {
    finish(false);
  }, [location.key, finish]);
  useEffect(() => {
    if (pending) dialog.current?.showModal();
  }, [pending]);
  const confirmAdd = useCallback((item: GuestItem): Promise<boolean> => {
    if (signedIn()) return Promise.resolve(true);
    active.current?.resolve(false);
    return new Promise((resolve) => {
      const next = { ...item, resolve };
      active.current = next;
      setPending(next);
    });
  }, []);
  return (
    <LoyaltyShoppingContext.Provider value={{ guest, confirmAdd }}>
      {children}
      <dialog
        ref={dialog}
        aria-labelledby="loyalty-add-title"
        aria-describedby="loyalty-add-description"
        className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-2xl border-0 bg-white p-0 text-[#0F172A] shadow-2xl backdrop:bg-black/60"
        onCancel={(event) => {
          event.preventDefault();
          finish(false);
        }}
        onClose={() => {
          if (active.current && !dialog.current?.open) finish(false);
        }}
        onKeyDown={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        {pending && (
          <>
            <div className="relative bg-[#264B9A] px-6 py-7 text-white">
              <button
                type="button"
                aria-label="Close personalized menu prompt"
                onClick={() => finish(false)}
                className="absolute top-2 right-2 flex h-11 w-11 items-center justify-center rounded-full text-2xl hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-[#ffd100]"
              >
                ×
              </button>
              <span className="font-poppins inline-block bg-[#ffd100] px-2 py-1 text-xs font-bold tracking-wider text-[#14294F] uppercase">
                Your Catalyst menu
              </span>
              <h2
                id="loyalty-add-title"
                className="font-futura mt-4 pr-5 text-3xl leading-tight font-bold"
              >
                Your menu.
                <br />
                More you.
              </h2>
              <p id="loyalty-add-description" className="mt-3 text-sm leading-relaxed">
                Sign in for a menu personalized to you, or keep shopping as a guest.
              </p>
            </div>
            <div className="space-y-4 p-6">
              <div className="border-b border-slate-200 pb-4">
                <p className="font-semibold">{pending.name}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {pending.quantity} × ${pending.price.toFixed(2)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  finish(false);
                  openLoyaltySignIn();
                }}
                className="min-h-12 w-full rounded-lg bg-[#264B9A] px-4 py-3 font-bold text-white hover:bg-[#1e3a7a] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#264B9A]"
              >
                Personalize my menu
              </button>
              <button
                type="button"
                onClick={() => finish(true)}
                className="min-h-12 w-full rounded-lg border-2 border-[#264B9A] px-4 py-3 font-semibold text-[#264B9A] hover:bg-blue-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#264B9A]"
              >
                Add as guest · ${(pending.price * pending.quantity).toFixed(2)}
              </button>
              <p className="text-center text-sm text-slate-600">
                New here?{' '}
                <Link
                  to="/register"
                  onClick={() => finish(false)}
                  className="font-semibold text-[#264B9A] underline underline-offset-4"
                >
                  Create an account
                </Link>
              </p>
            </div>
          </>
        )}
      </dialog>
    </LoyaltyShoppingContext.Provider>
  );
}
export const useLoyaltyShopping = () => useContext(LoyaltyShoppingContext);
